import {Component, forwardRef, HostListener, Input, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DataService} from '../../../Services/data.service';
import {take} from 'rxjs/operators';

interface AccountGroup {
  label: string;
  accounts: any[];
}

@Component({
  selector: 'app-account-grouped-list-select',
  templateUrl: './account-grouped-list-select.component.html',
  styleUrls: ['./account-grouped-list-select.component.css'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccountGroupedListSelectComponent),
      multi: true,
    }
  ]
})
export class AccountGroupedListSelectComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder = '— Select Account —';
  @Input() cssClass = 'form-select';

  accountGroups: AccountGroup[] = [];
  filteredGroups: AccountGroup[] = [];
  value: string = '';
  selectedLabel: string = '';
  isOpen = false;
  isDisabled = false;
  highlightedIndex = -2; // -2 = none, -1 = placeholder row, 0+ = flat account index

  private flatAccounts: any[] = [];

  private onChange: (val: string) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  constructor(private tableDataService: DataService) {
  }

  ngOnInit() {
    this.loadAccounts();
  }

  loadAccounts() {
    this.tableDataService.getApiListData('account?pageNumber=0&pageSize=999999')
      .pipe(take(1))
      .subscribe({
        next: (result: any) => {
          const accounts: any[] = result.Data || [];
          this.buildGroups(accounts);
          // If a value was already written before data loaded, resolve the label
          if (this.value) {
            this.resolveLabel(accounts);
          }
        }
      });
  }

  private buildGroups(accounts: any[]) {
    const groupMap: { [typeName: string]: AccountGroup } = {};
    const typeOrder: string[] = [];

    for (const acct of accounts) {
      const typeName = acct.type_name || 'Other';
      if (!groupMap[typeName]) {
        groupMap[typeName] = {label: typeName, accounts: []};
        typeOrder.push(typeName);
      }
      groupMap[typeName].accounts.push(acct);
    }

    this.accountGroups = typeOrder.map(t => groupMap[t]);
    this.filteredGroups = [...this.accountGroups];
  }

  private resolveLabel(accounts: any[]) {
    const match = accounts.find(a => a.id === this.value);
    this.selectedLabel = match ? match.number_name : '';
  }

  toggleDropdown() {
    if (this.isDisabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.highlightedIndex = -2;
      this.buildFlatAccounts();
    } else {
      this.onTouched();
    }
  }

  // Build a flat list of all visible accounts for keyboard nav
  private buildFlatAccounts() {
    this.flatAccounts = this.filteredGroups.flatMap(g => g.accounts);
  }

  // Returns the flat index of an account in the current filtered list
  flatIndex(acct: any): number {
    return this.flatAccounts.indexOf(acct);
  }

  onSearchKeydown(event: KeyboardEvent) {
    const total = this.flatAccounts.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.highlightedIndex < total - 1) {
        this.highlightedIndex++;
      }
      this.scrollHighlightedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.highlightedIndex > -1) {
        this.highlightedIndex--;
      }
      this.scrollHighlightedIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.highlightedIndex === -1) {
        this.selectAccount('', '');
      } else if (this.highlightedIndex >= 0 && this.highlightedIndex < total) {
        const acct = this.flatAccounts[this.highlightedIndex];
        this.selectAccount(acct.id, acct.number_name);
      }
    } else if (event.key === 'Escape') {
      this.isOpen = false;
      this.onTouched();
    }
  }

  private scrollHighlightedIntoView() {
    // Use setTimeout to let Angular render the highlighted class first
    setTimeout(() => {
      const el = document.querySelector(`[data-flat-index="${this.highlightedIndex}"]`) as HTMLElement;
      if (el) {
        el.scrollIntoView({block: 'nearest'});
      }
    });
  }

  onSearch(term: string) {
    const lower = term.toLowerCase();
    if (!lower) {
      this.filteredGroups = [...this.accountGroups];
      return;
    }
    this.filteredGroups = this.accountGroups
      .map(group => ({
        label: group.label,
        accounts: group.accounts.filter(a =>
          (a.number_name || '').toLowerCase().includes(lower)
        )
      }))
      .filter(group => group.accounts.length > 0);
    this.buildFlatAccounts();
    this.highlightedIndex = -2;
  }

  selectAccount(id: string, label: string) {
    this.value = id;
    this.selectedLabel = label;
    this.isOpen = false;
    this.highlightedIndex = -2;
    this.filteredGroups = [...this.accountGroups];
    this.buildFlatAccounts();
    this.onChange(id);
    this.onTouched();
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('app-account-grouped-list-select')) {
      if (this.isOpen) {
        this.isOpen = false;
        this.onTouched();
      }
    }
  }

  // ControlValueAccessor
  writeValue(val: string) {
    this.value = val || '';
    // Resolve label if groups already loaded
    if (this.accountGroups.length > 0) {
      const allAccounts = this.accountGroups.flatMap(g => g.accounts);
      this.resolveLabel(allAccounts);
    }
  }

  registerOnChange(fn: (val: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
